///
/// EarthBrowser
///
/// Jet Stream Field Generator
///
/// Packs a pair of 250 hPa u/v EBWX wind planes into an RGBA8 texture for
/// the jet stream shell shader (jetstream_sphere.glsl):
///   R = u component, G = v component   (signed, centered on 128;
///                                       cJetComponentRangeMps spans 0..255)
///   B = wind speed                      (0..cJetSpeedRangeMps -> 0..255)
///   A = 255 where both components are valid, 0 otherwise
///
/// 8-bit components give ~1 m/s steps across the +/-125 m/s range, which is
/// below anything visible in the flow rendering, and keep the frames to
/// 1 MB at 0.5 deg with universally supported linear filtering (no float
/// texture extensions needed).
///
/// Created by Matt Giger on 10/23/2026
/// Copyright (c) 2026 Geodata Labs
///

import type { EBWXPlane } from "./EBWXDecoder"

/** Component encoding half-range: byte 0 = -range, 128 = 0, 255 = +range */
export const cJetComponentRangeMps = 125
/** Speed encoding full range: byte 255 = this speed */
export const cJetSpeedRangeMps = 125

/** A jet streak core: local wind-speed maximum worth labelling */
export interface JetMaximum {
	lon: number
	lat: number
	speedMps: number
}

export interface JetFieldResult {
	data: Uint8Array
	width: number
	height: number
	/** Peak 250 hPa wind speed in the plane (m/s) */
	maxSpeedMps: number
	/** Jet streak cores, strongest first */
	maxima: JetMaximum[]
}

// Streak cores: speed floor, local-max window half-width in cells, minimum
// separation between labelled cores, and the cap on labels per frame
const cMaximumMinSpeedMps = 45
const cMaximumWindowCells = 4
const cMaximumSeparationKm = 1800
const cMaxMaxima = 14
const cMaximumMaxLatDeg = 75

export function generateJetField(u: EBWXPlane, v: EBWXPlane): JetFieldResult | null {
	if (u.nx !== v.nx || u.ny !== v.ny) return null

	const n = u.nx * u.ny
	const out = new Uint8Array(n * 4)
	const speeds = new Float32Array(n)
	let maxSpeed = 0
	let hasValid = false

	for (let i = 0; i < n; i++) {
		const uu = u.data[i]
		const vv = v.data[i]
		const o = i * 4
		if (isNaN(uu) || isNaN(vv)) {
			out[o] = 128
			out[o + 1] = 128
			out[o + 2] = 0
			out[o + 3] = 0
			speeds[i] = -1
			continue
		}
		const speed = Math.sqrt(uu * uu + vv * vv)
		speeds[i] = speed
		if (speed > maxSpeed) maxSpeed = speed
		out[o] = encodeComponent(uu)
		out[o + 1] = encodeComponent(vv)
		out[o + 2] = Math.min(255, Math.round(speed / cJetSpeedRangeMps * 255))
		out[o + 3] = 255
		hasValid = true
	}

	if (!hasValid) return null
	return { data: out, width: u.nx, height: u.ny, maxSpeedMps: maxSpeed, maxima: findMaxima(speeds, u) }
}

/** Local speed maxima above the floor, thinned by separation, strongest first */
function findMaxima(speeds: Float32Array, geo: EBWXPlane): JetMaximum[] {
	const { nx, ny, west, south, resolution } = geo
	const north = south + (ny - 1) * resolution
	const w = cMaximumWindowCells
	const candidates: JetMaximum[] = []

	for (let row = w; row < ny - w; row++) {
		const lat = north - row * resolution
		if (Math.abs(lat) > cMaximumMaxLatDeg) continue
		for (let col = 0; col < nx; col++) {
			const s = speeds[row * nx + col]
			if (s < cMaximumMinSpeedMps) continue
			let isMax = true
			for (let dr = -w; dr <= w && isMax; dr++) {
				const rowOff = (row + dr) * nx
				for (let dc = -w; dc <= w; dc++) {
					if (dr === 0 && dc === 0) continue
					if (speeds[rowOff + ((col + dc + nx) % nx)] > s) {
						isMax = false
						break
					}
				}
			}
			if (!isMax) continue
			let lon = west + col * resolution
			if (lon > 180) lon -= 360
			candidates.push({ lon, lat, speedMps: s })
		}
	}

	candidates.sort((a, b) => b.speedMps - a.speedMps)
	const kept: JetMaximum[] = []
	for (const c of candidates) {
		if (kept.every(k => haversineKm(c.lon, c.lat, k.lon, k.lat) >= cMaximumSeparationKm)) {
			kept.push(c)
			if (kept.length >= cMaxMaxima) break
		}
	}
	return kept
}

function haversineKm(lon1: number, lat1: number, lon2: number, lat2: number): number {
	const rad = Math.PI / 180
	const phi1 = lat1 * rad
	const phi2 = lat2 * rad
	let dLon = lon2 - lon1
	if (dLon > 180) dLon -= 360
	if (dLon < -180) dLon += 360
	const s1 = Math.sin((phi2 - phi1) / 2)
	const s2 = Math.sin(dLon * rad / 2)
	const a = s1 * s1 + Math.cos(phi1) * Math.cos(phi2) * s2 * s2
	return 6371 * 2 * Math.asin(Math.min(1, Math.sqrt(a)))
}

function encodeComponent(mps: number): number {
	const t = mps / cJetComponentRangeMps * 127.5 + 128
	return Math.max(0, Math.min(255, Math.round(t)))
}
